import {Button, ButtonProps, IconButton, IconButtonProps} from "@mui/material";
import {useEffect, useState} from "react";
import UAParser from "ua-parser-js";
import ya from "react-yandex-metrika";
import {useCurrentUser} from "~/auth/hooks";
import {SignInDialogFullscreen} from "~/auth/components/SignInDialogFullscreen/SignInDialogFullscreen";
import {Feedback} from "~/ui/icons";
import {FEEDBACK_EMAIL, VERSION} from "~/config";
import {useHistory, useLocation} from "react-router-dom";
import {isForbiddenError, isServiceUnavailableError} from "~/apollo/errors";
import {ErrorHandlerProvider, useErrorHandler} from "~/components/ErrorHandling";



type CreateHrefOptions = {
    body?: string
    subject?: string
    copyTo?: string | string[]
    hiddenCopyTo?: string | string[]
}
function createHref(to: string, options?: CreateHrefOptions) {
    function toArray(a) {
        if (a == null) {
            return null;
        }
        return Array.isArray(a) ? a : [a];
    }

    const params = [];
    if (options?.body) {
        params.push(`body=${encodeURIComponent(options.body)}`);
    }
    if (options?.subject) {
        params.push(`subject=${encodeURIComponent(options.subject)}`);
    }
    if (options?.copyTo) {
        params.push(`cc=${encodeURIComponent(toArray(options.copyTo).join(", "))}`);
    }
    if (options?.hiddenCopyTo) {
        params.push(`bcc=${encodeURIComponent(toArray(options.hiddenCopyTo).join(", "))}`)
    }

    return `mailto:${encodeURIComponent(to)}?${params.join("&")}`
}


export type FeedbackButtonProps = Omit<ButtonProps, "children" | "onClick" | "component" | "href" | "variant" | "color"> & {
    variant?: "icon" | "button"
    color?: ButtonProps["color"] & IconButtonProps["color"]
};

const defaultProps: Partial<FeedbackButtonProps> = {
    variant: "button"
};

export const FeedbackButton = (props: FeedbackButtonProps) => {
    const {variant, ...buttonProps} = props;
    const parentErrorHandler = useErrorHandler();
    const [user] = useCurrentUser();
    const history = useHistory();
    const location = useLocation();
    const [yandexClientID, setYandexClientID] = useState(null);
    const openSignIn = new URLSearchParams(location.search).has("signin");
    const [openEmailClient, setOpenEmailClient] = useState(false);

    useEffect(() => {
        if (!openEmailClient || !user || !yandexClientID) {
            return;
        }
        const subject = "Порт в кармане";
        const uaResult = new UAParser().getResult();
        const body = [
            '**********************************',
            'Данный заголовок сформирован автоматически, просьба не редактировать',
            '',
            `Пользователь: ${user?.userName || "-"}`,
            `Компания: ${user?.companyName || "-"}`,
            `ИНН: ${user?.inn || "-"}`,
            `КПП: ${user?.kpp || "-"}`,
            `Браузер: ${uaResult.browser.name} (${uaResult.browser.version})`,
            `Экран: ${window?.innerWidth}x${window?.innerHeight}, DPI: ${window?.devicePixelRatio || '-'}`,
            `Устройство: ${[uaResult.device.vendor, uaResult.device.model].filter(Boolean).join(" ") || "-"}`,
            `Операционная система: ${uaResult.os.name} (${uaResult.os.version})`,
            `Platform: ${VERSION || "-"}`,
            `Яндекс ClientID: ${yandexClientID}`,
            '**********************************',
            '',
            'Текст вашего обращения:',
            '',
        ].join("\n");
        const href = createHref(FEEDBACK_EMAIL, {subject: subject, body: body});
        window.open(href, "_blank");
        setOpenEmailClient(false);
    }, [openEmailClient, user, yandexClientID]);

    useEffect(() => {
        ya("getClientID", clientID => {
            setYandexClientID(clientID);
        });
    }, []);

    const handleClick = () => {
        setOpenEmailClient(true);
        if (!user) {
            const params = new URLSearchParams(history.location.search);
            params.set("signin", "");

            history.push({
                ...history.location,
                search: params.toString()
            });
        }
    }

    const handleSignInComplete = () => {
        history.goBack();
    }

    const handleSignInClose = () => {
        setOpenEmailClient(false);
        history.goBack();
    }

    const handleError = (error: Error) => {
        if (isForbiddenError(error)) {
            handleSignInClose();
        } else if (isServiceUnavailableError(error)) {
            handleSignInClose();
        }
        parentErrorHandler(error);
    }

    let button = (
        <Button {...buttonProps} onClick={handleClick}>
            Обратиться в техподдержку
        </Button>
    );
    if (variant == "icon") {
        button = (
            <IconButton {...buttonProps} onClick={handleClick}>
                <Feedback/>
            </IconButton>
        );
    }

    return (
        <>
            {button}
            <ErrorHandlerProvider value={handleError}>
                <SignInDialogFullscreen open={openSignIn} onComplete={handleSignInComplete} onClose={handleSignInClose}/>
            </ErrorHandlerProvider>
        </>
    )
}
FeedbackButton.defaultProps = defaultProps;